import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useHotkeys } from 'react-hotkeys-hook';
import { apiClient } from '../api/client';
import { GradingStatus, type Image, type PreviewDescriptor } from '../api/types';
import { useImageZoom } from '../hooks/useImageZoom';
import { useAsyncImage } from '../hooks/useAsyncImage';
import { ensurePreviewReady } from '../hooks/previewPoll';
import { useColorPreview } from '../hooks/useColorPreview';

interface ImageComparisonViewProps {
  dbId: string;
  leftImageId: number;
  rightImageId: number | null;
  onClose: () => void;
  onSelectRightImage: () => void;
  onGradeLeft: (status: 'accepted' | 'rejected' | 'pending') => void;
  onGradeRight: (status: 'accepted' | 'rejected' | 'pending') => void;
  onNavigateRightNext: () => void;
  onNavigateRightPrev: () => void;
  onSwapImages: () => void;
  canGrade?: boolean;
}

export default function ImageComparisonView({
  dbId,
  leftImageId,
  rightImageId,
  onClose,
  onSelectRightImage,
  onGradeLeft,
  onGradeRight,
  onNavigateRightNext,
  onNavigateRightPrev,
  onSwapImages,
  canGrade = true,
}: ImageComparisonViewProps) {
  const [showStars, setShowStars] = useState(false);
  // Same shared preference as the grid and the detail view: comparing two
  // frames is pointless if one arrives rendered differently from the other.
  const color = useColorPreview();
  const [syncZoom, setSyncZoom] = useState(true);
  const [maxStars] = useState(1000);
  
  // Initialize zoom for both images
  const leftZoom = useImageZoom({ minScale: 0.1, maxScale: 10.0 });
  const rightZoom = useImageZoom({ minScale: 0.1, maxScale: 10.0 });
  
  // Track image loading
  const [leftImageLoaded, setLeftImageLoaded] = useState(false);
  const [rightImageLoaded, setRightImageLoaded] = useState(false);
  const [loadedLeftSrc, setLoadedLeftSrc] = useState<string | null>(null);
  const [loadedRightSrc, setLoadedRightSrc] = useState<string | null>(null);
  
  // Track original image preloading
  const [leftOriginalLoaded, setLeftOriginalLoaded] = useState(false);
  const [rightOriginalLoaded, setRightOriginalLoaded] = useState(false);
  const leftOriginalRef = useRef<HTMLImageElement | null>(null);
  const rightOriginalRef = useRef<HTMLImageElement | null>(null);
  
  // Track whether to use original images
  const [useLeftOriginal, setUseLeftOriginal] = useState(false);
  const [useRightOriginal, setUseRightOriginal] = useState(false);
  const leftDimensionsRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });
  const rightDimensionsRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });
  
  // State machine to prevent feedback loops
  const leftImageStateRef = useRef<'large' | 'switching-to-original' | 'original'>('large');
  const rightImageStateRef = useRef<'large' | 'switching-to-original' | 'original'>('large');

  // Fetch left image
  const { data: leftImage } = useQuery({
    queryKey: ['db', dbId, 'image', leftImageId],
    queryFn: () => apiClient.getImage(dbId, leftImageId),
  });

  // Fetch right image (if selected)
  const { data: rightImage, isFetching: isRightImageFetching } = useQuery({
    queryKey: ['db', dbId, 'image', rightImageId],
    queryFn: () =>
      rightImageId ? apiClient.getImage(dbId, rightImageId) : Promise.resolve(null),
    enabled: !!rightImageId,
    placeholderData: (previousData) => previousData, // Keep showing previous image while loading new one
  });

  // Keyboard shortcuts
  useHotkeys('escape', onClose, [onClose]);
  useHotkeys('s', () => setShowStars(s => !s), []);
  useHotkeys('z', () => setSyncZoom(s => !s), []);
  
  // Grade left image
  useHotkeys('1', () => onGradeLeft('accepted'), [onGradeLeft]);
  useHotkeys('2', () => onGradeLeft('rejected'), [onGradeLeft]);
  useHotkeys('3', () => onGradeLeft('pending'), [onGradeLeft]);
  
  // Grade right image
  useHotkeys('7', () => rightImageId && onGradeRight('accepted'), [rightImageId, onGradeRight]);
  useHotkeys('8', () => rightImageId && onGradeRight('rejected'), [rightImageId, onGradeRight]);
  useHotkeys('9', () => rightImageId && onGradeRight('pending'), [rightImageId, onGradeRight]);
  
  // Navigate right image
  useHotkeys('right,k', onNavigateRightNext, [onNavigateRightNext]);
  useHotkeys('left,j', onNavigateRightPrev, [onNavigateRightPrev]);
  
  // Swap images (prevent swapping identical images)
  useHotkeys('w', () => rightImageId && leftImageId !== rightImageId && onSwapImages(), [rightImageId, leftImageId, onSwapImages]);

  // Reset loaded state when images change
  useEffect(() => {
    setLeftImageLoaded(false);
    leftZoom.resetInitialization();
  }, [leftImageId, showStars, leftZoom]);

  useEffect(() => {
    setRightImageLoaded(false);
    rightZoom.resetInitialization();
  }, [rightImageId, showStars, rightZoom]);


  // Store the target zoom level when user zooms
  const targetRightZoomRef = useRef<number | null>(null);
  const targetLeftZoomRef = useRef<number | null>(null);

  // Async loading for both comparison images (poll-on-error + "Generating…").
  // The size each pane requests must match its <img> below so the readiness
  // poll addresses the same cache file.
  const leftSize: 'large' | 'original' =
    useLeftOriginal ||
    (leftOriginalLoaded && targetLeftZoomRef.current !== null && targetLeftZoomRef.current > 1.0)
      ? 'original'
      : 'large';
  const leftSrc = showStars
    ? apiClient.getAnnotatedUrl(dbId, leftImageId, leftSize, maxStars)
    : apiClient.getPreviewUrl(dbId, leftImageId, { size: leftSize, color });
  const leftDescriptor: PreviewDescriptor = showStars
    ? { imageId: leftImageId, kind: 'annotated', size: leftSize, maxStars }
    : { imageId: leftImageId, kind: 'preview', size: leftSize, color };
  const asyncLeft = useAsyncImage(dbId, leftSrc, leftDescriptor);

  const rightSize: 'large' | 'original' =
    useRightOriginal || useLeftOriginal ? 'original' : 'large';
  const rightIdForUrl = rightImageId ?? 0;
  const rightSrc = showStars
    ? apiClient.getAnnotatedUrl(dbId, rightIdForUrl, rightSize, maxStars)
    : apiClient.getPreviewUrl(dbId, rightIdForUrl, { size: rightSize, color });
  const rightDescriptor: PreviewDescriptor = showStars
    ? { imageId: rightIdForUrl, kind: 'annotated', size: rightSize, maxStars }
    : { imageId: rightIdForUrl, kind: 'preview', size: rightSize, color };
  const asyncRight = useAsyncImage(dbId, rightSrc, rightDescriptor);
  const hideLeftImage = asyncLeft.state !== 'ready' || loadedLeftSrc !== asyncLeft.src;
  const hideRightImage = asyncRight.state !== 'ready' || loadedRightSrc !== asyncRight.src;

  // Capture zoom intent before image change
  useEffect(() => {
    const visualScale = rightZoom.getVisualScale();
    if (visualScale > 1.2 || visualScale < 0.8) {
      targetRightZoomRef.current = visualScale;
    }
  }, [rightZoom.zoomState.scale, rightZoom]);
  
  useEffect(() => {
    const visualScale = leftZoom.getVisualScale();
    if (visualScale > 1.2 || visualScale < 0.8) {
      targetLeftZoomRef.current = visualScale;
    }
  }, [leftZoom.zoomState.scale, leftZoom]);
  
  // Fit images when they load
  useEffect(() => {
    if (leftImageLoaded) {
      if (targetLeftZoomRef.current === null) {
        leftZoom.zoomToFit();
      }
      // Otherwise preserve existing zoom
    }
  }, [leftImageLoaded, leftZoom]);
  
  useEffect(() => {
    if (rightImageLoaded) {
      if (targetRightZoomRef.current === null) {
        rightZoom.zoomToFit();
      }
    }
  }, [rightImageLoaded, rightZoom]);
  
  // Force switch to original when image loads with high zoom
  useEffect(() => {
    if (rightImageLoaded && rightOriginalLoaded && !useRightOriginal) {
      // Use stored target zoom instead of current zoom which might be reset
      // OR if left is using original, right should match
      if ((targetRightZoomRef.current && targetRightZoomRef.current > 1.0) || useLeftOriginal) {
        // We have high zoom and original is ready but not being used - switch now
        setUseRightOriginal(true);
        rightImageStateRef.current = 'original';
      }
    }
  }, [rightImageLoaded, rightOriginalLoaded, useRightOriginal, useLeftOriginal]);
  
  useEffect(() => {
    if (leftImageLoaded && leftOriginalLoaded && !useLeftOriginal) {
      // Use stored target zoom instead of current zoom which might be reset
      if (targetLeftZoomRef.current && targetLeftZoomRef.current > 1.0) {
        // We have high zoom and original is ready but not being used - switch now
        setUseLeftOriginal(true);
        leftImageStateRef.current = 'original';
      }
    }
  }, [leftImageLoaded, leftOriginalLoaded, useLeftOriginal]);
  
  // Load original dimensions from metadata if available
  useEffect(() => {
    // Try different possible field names for image dimensions
    const width = leftImage?.metadata?.ImageWidth || 
                  leftImage?.metadata?.NAXIS1 || 
                  leftImage?.metadata?.ImageSize?.[0];
    const height = leftImage?.metadata?.ImageHeight || 
                   leftImage?.metadata?.NAXIS2 || 
                   leftImage?.metadata?.ImageSize?.[1];
    
    if (width && height && typeof width === 'number' && typeof height === 'number') {
      leftZoom.setImageDimensions(width, height, true);
    }
  }, [leftImage, leftZoom]);
  
  // Combined effect for left image preloading and switching
  useEffect(() => {
    if (!leftImage) return;
    
    const visualScale = leftZoom.getVisualScale();
    const state = leftImageStateRef.current;
    
    // Preload when visual zoom > 80%, or immediately if zoom > 100% (preserved from previous image)
    // Also check stored target zoom in case current zoom is reset
    const shouldPreload = (visualScale > 0.8 && state === 'large') || 
                         (visualScale > 1.0 && state === 'large') ||
                         (targetLeftZoomRef.current && targetLeftZoomRef.current > 0.8 && state === 'large');
    
    if (shouldPreload && !leftOriginalLoaded && !leftOriginalRef.current) {
      leftOriginalRef.current = new Image(); // guard: preload only once
      const originalUrl = showStars
        ? apiClient.getAnnotatedUrl(dbId, leftImageId, 'original', maxStars)
        : apiClient.getPreviewUrl(dbId, leftImageId, { size: 'original', color });
      const descriptor: PreviewDescriptor = showStars
        ? { imageId: leftImageId, kind: 'annotated', size: 'original', maxStars }
        : { imageId: leftImageId, kind: 'preview', size: 'original', color };

      // Route through the interactive queue so an uncached 'original' is
      // generated (its 202 is not a failure); only flip once it's ready.
      void ensurePreviewReady(dbId, originalUrl, descriptor).then((ok) => {
        if (!ok) return;
        setLeftOriginalLoaded(true);

        // If we were waiting for original due to high zoom, switch immediately
        const currentVisualScale = leftZoom.getVisualScale();
        const currentState = leftImageStateRef.current;
        if (currentState === 'large' && currentVisualScale > 1.0) {
          leftImageStateRef.current = 'switching-to-original';
          setUseLeftOriginal(true);
        }
      });
    }
    
    // State machine transitions based on visual scale - only switch to original, never back
    if (state === 'large' && visualScale > 1.0 && leftOriginalLoaded) {
      // Switch to original
      leftImageStateRef.current = 'switching-to-original';
      setUseLeftOriginal(true);
    }
    // Never switch back from original to large
  }, [leftZoom, leftImageId, showStars, leftImage, leftOriginalLoaded, dbId, maxStars, color]);
  
  // Load original dimensions from metadata if available
  useEffect(() => {
    // Try different possible field names for image dimensions
    const width = rightImage?.metadata?.ImageWidth || 
                  rightImage?.metadata?.NAXIS1 || 
                  rightImage?.metadata?.ImageSize?.[0];
    const height = rightImage?.metadata?.ImageHeight || 
                   rightImage?.metadata?.NAXIS2 || 
                   rightImage?.metadata?.ImageSize?.[1];
    
    if (width && height && typeof width === 'number' && typeof height === 'number') {
      rightZoom.setImageDimensions(width, height, true);
    }
  }, [rightImage, rightZoom]);
  
  // Combined effect for right image preloading and switching
  useEffect(() => {
    if (!rightImage || !rightImageId) return;
    
    const visualScale = rightZoom.getVisualScale();
    const state = rightImageStateRef.current;
    
    // Preload when visual zoom > 80%, or immediately if zoom > 100% (preserved from previous image)
    // Also check stored target zoom in case current zoom is reset
    // Most importantly: if left is using original, right should too
    const shouldPreload = (visualScale > 0.8 && state === 'large') || 
                         (visualScale > 1.0 && state === 'large') ||
                         (targetRightZoomRef.current && targetRightZoomRef.current > 0.8 && state === 'large') ||
                         (useLeftOriginal && state === 'large');
    
    if (shouldPreload && !rightOriginalLoaded && !rightOriginalRef.current && rightImageId) {
      rightOriginalRef.current = new Image(); // guard: preload only once
      const originalUrl = showStars
        ? apiClient.getAnnotatedUrl(dbId, rightImageId, 'original', maxStars)
        : apiClient.getPreviewUrl(dbId, rightImageId, { size: 'original', color });
      const descriptor: PreviewDescriptor = showStars
        ? { imageId: rightImageId, kind: 'annotated', size: 'original', maxStars }
        : { imageId: rightImageId, kind: 'preview', size: 'original', color };

      void ensurePreviewReady(dbId, originalUrl, descriptor).then((ok) => {
        if (!ok) return;
        setRightOriginalLoaded(true);

        // If we were waiting for original due to high zoom OR left is using original, switch immediately
        const currentVisualScale = rightZoom.getVisualScale();
        const currentState = rightImageStateRef.current;
        if (currentState === 'large' && (currentVisualScale > 1.0 || useLeftOriginal)) {
          rightImageStateRef.current = 'switching-to-original';
          setUseRightOriginal(true);
        }
      });
    }
    
    // State machine transitions based on visual scale OR left image state
    if (state === 'large' && ((visualScale > 1.0 && rightOriginalLoaded) || (useLeftOriginal && rightOriginalLoaded))) {
      // Switch to original
      rightImageStateRef.current = 'switching-to-original';
      setUseRightOriginal(true);
    }
    // Never switch back from original to large
  }, [
    rightZoom,
    rightImageId,
    showStars,
    rightImage,
    rightOriginalLoaded,
    useLeftOriginal,
    dbId,
    maxStars,
    color,
  ]);
  
  // Effect to make right image follow left image's resolution choice
  useEffect(() => {
    if (useLeftOriginal && !useRightOriginal && rightOriginalLoaded && rightImageStateRef.current !== 'switching-to-original') {
      rightImageStateRef.current = 'switching-to-original';
      setUseRightOriginal(true);
    }
  }, [useLeftOriginal, useRightOriginal, rightOriginalLoaded]);

  // Reset preload state when images change
  useLayoutEffect(() => {
    setLeftOriginalLoaded(false);
    leftOriginalRef.current = null;
    setUseLeftOriginal(false);
    setLoadedLeftSrc(null);
    leftDimensionsRef.current = { width: 0, height: 0 };
    leftImageStateRef.current = 'large';
  }, [leftImageId, showStars, color]);
  
  useLayoutEffect(() => {
    setRightOriginalLoaded(false);
    rightOriginalRef.current = null;
    setUseRightOriginal(false);
    setLoadedRightSrc(null);
    rightDimensionsRef.current = { width: 0, height: 0 };
    rightImageStateRef.current = 'large';
  }, [rightImageId, showStars, color]);

  // Sync zoom states when syncZoom is enabled
  useEffect(() => {
    if (syncZoom) {
      // Sync right zoom to match left zoom whenever left changes
      rightZoom.setZoomState(leftZoom.zoomState);
    }
  }, [syncZoom, leftZoom.zoomState, rightZoom]);

  // Handle zoom events
  const handleLeftZoom = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
    leftZoom.handleWheel(e);
  }, [leftZoom]);

  const handleRightZoom = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
    if (!syncZoom) {
      rightZoom.handleWheel(e);
    } else {
      // When synced, translate coordinates and apply to left image
      const leftContainer = leftZoom.containerRef.current;
      const rightContainer = rightZoom.containerRef.current;
      if (leftContainer && rightContainer) {
        const rightRect = rightContainer.getBoundingClientRect();
        const leftRect = leftContainer.getBoundingClientRect();
        
        // Create a new wheel event with translated coordinates
        const adjustedEvent = Object.create(e);
        adjustedEvent.clientX = e.clientX - rightRect.left + leftRect.left;
        adjustedEvent.clientY = e.clientY - rightRect.top + leftRect.top;
        
        leftZoom.handleWheel(adjustedEvent);
      }
    }
  }, [leftZoom, rightZoom, syncZoom]);
  
  // Handle mouse move for panning
  const handleLeftMouseMove = useCallback((e: React.MouseEvent) => {
    leftZoom.handleMouseMove(e);
  }, [leftZoom]);
  
  const handleRightMouseMove = useCallback((e: React.MouseEvent) => {
    if (!syncZoom) {
      rightZoom.handleMouseMove(e);
    } else {
      // When synced, translate coordinates and apply to left image
      const leftContainer = leftZoom.containerRef.current;
      const rightContainer = rightZoom.containerRef.current;
      if (leftContainer && rightContainer) {
        const rightRect = rightContainer.getBoundingClientRect();
        const leftRect = leftContainer.getBoundingClientRect();
        
        // Create a new mouse event with translated coordinates
        const adjustedEvent = Object.create(e);
        adjustedEvent.clientX = e.clientX - rightRect.left + leftRect.left;
        adjustedEvent.clientY = e.clientY - rightRect.top + leftRect.top;
        
        leftZoom.handleMouseMove(adjustedEvent);
      }
    }
  }, [leftZoom, rightZoom, syncZoom]);

  const routeRightTouch = useCallback((
    e: React.TouchEvent,
    phase: 'start' | 'move' | 'end'
  ) => {
    if (!syncZoom) {
      if (phase === 'start') rightZoom.handleTouchStart(e);
      if (phase === 'move') rightZoom.handleTouchMove(e);
      if (phase === 'end') rightZoom.handleTouchEnd(e);
      return;
    }

    const leftContainer = leftZoom.containerRef.current;
    const rightContainer = rightZoom.containerRef.current;
    if (!leftContainer || !rightContainer) return;

    const leftRect = leftContainer.getBoundingClientRect();
    const rightRect = rightContainer.getBoundingClientRect();
    const touches = Array.from(e.touches, touch => ({
      clientX: touch.clientX - rightRect.left + leftRect.left,
      clientY: touch.clientY - rightRect.top + leftRect.top,
    }));
    const adjustedEvent = {
      touches,
      preventDefault: () => e.preventDefault(),
      stopPropagation: () => e.stopPropagation(),
    } as unknown as React.TouchEvent;

    if (phase === 'start') leftZoom.handleTouchStart(adjustedEvent);
    if (phase === 'move') leftZoom.handleTouchMove(adjustedEvent);
    if (phase === 'end') leftZoom.handleTouchEnd(adjustedEvent);
  }, [leftZoom, rightZoom, syncZoom]);

  const getStatusClass = (image: Image | null | undefined) => {
    if (!image) return '';
    switch (image.grading_status) {
      case GradingStatus.Accepted:
        return 'status-accepted';
      case GradingStatus.Rejected:
        return 'status-rejected';
      default:
        return 'status-pending';
    }
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp * 1000).toLocaleString();
  };

  if (!leftImage) {
    return <div className="comparison-overlay"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="comparison-overlay">
      <div className="comparison-container">
        <div className="comparison-header">
          <h2>Image Comparison</h2>
          <div className="comparison-controls">
            <label>
              <input
                type="checkbox"
                checked={syncZoom}
                onChange={(e) => setSyncZoom(e.target.checked)}
              />
              Sync Zoom
            </label>
            <label>
              <input
                type="checkbox"
                checked={showStars}
                onChange={(e) => setShowStars(e.target.checked)}
              />
              Show Stars
            </label>
            {rightImageId && (
              <button
                className="swap-button"
                onClick={onSwapImages}
                disabled={leftImageId === rightImageId}
                title={leftImageId === rightImageId ? "Cannot swap identical images" : "Swap left and right images (W)"}
              >
                ↔ Swap
              </button>
            )}
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="comparison-content">
          {/* Left Image */}
          <div className="comparison-panel">
            <div className="panel-header">
              <div className="panel-title-row">
                <h3>Left Image</h3>
                <div className={`status-banner ${getStatusClass(leftImage)}`}>
                  {leftImage.grading_status === GradingStatus.Accepted && '✓ ACCEPTED'}
                  {leftImage.grading_status === GradingStatus.Rejected && '✗ REJECTED'}
                  {leftImage.grading_status === GradingStatus.Pending && '○ PENDING'}
                </div>
              </div>
            </div>
            
            <div className="panel-image">
              <div 
                className={`zoom-container ${leftZoom.hasOverflow ? 'zoomed' : ''}`}
                ref={leftZoom.containerRef}
                onWheel={handleLeftZoom}
                onMouseDown={leftZoom.handleMouseDown}
                onMouseMove={handleLeftMouseMove}
                onMouseUp={leftZoom.handleMouseUp}
                onMouseLeave={leftZoom.handleMouseUp}
                onTouchStart={leftZoom.handleTouchStart}
                onTouchMove={leftZoom.handleTouchMove}
                onTouchEnd={leftZoom.handleTouchEnd}
                onTouchCancel={leftZoom.handleTouchEnd}
              >
                {asyncLeft.state === 'error' ? (
                  <div className="comparison-image-error" style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '400px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px'
                  }}>
                    <div style={{
                      textAlign: 'center',
                      color: 'var(--color-text-muted)'
                    }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: '12px' }}>
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                      </svg>
                      <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-text-hint)' }}>{asyncLeft.error ?? 'Image not available'}</p>
                    </div>
                  </div>
                ) : (
                  <img
                    ref={leftZoom.imageRef}
                    src={asyncLeft.src}
                    className={hideLeftImage ? 'comparison-image-hidden' : undefined}
                    alt={`${leftImage.target_name} - ${leftImage.filter_name || 'No filter'}`}
                    onError={asyncLeft.onError}
                    onLoad={(e) => {
                      asyncLeft.onLoad();
                      setLeftImageLoaded(true);
                      setLoadedLeftSrc(asyncLeft.src);

                      const img = e.currentTarget;
                      const newWidth = img.naturalWidth;
                      const newHeight = img.naturalHeight;
                      const oldWidth = leftDimensionsRef.current.width;
                      const oldHeight = leftDimensionsRef.current.height;

                      // Update image dimensions in zoom hook
                      const isShowingOriginal = useLeftOriginal || (leftOriginalLoaded && targetLeftZoomRef.current !== null && targetLeftZoomRef.current > 1.0);
                      leftZoom.setImageDimensions(newWidth, newHeight, isShowingOriginal);
                      // Recalibrate pan constraints to the loaded bitmap even on
                      // the paths below that adjust nothing (preserved zoom on a
                      // new image) — constraints follow stateDims, not the <img>.
                      leftZoom.notifyBitmapDimensions(newWidth, newHeight);

                      // Check if dimensions actually changed
                      const dimensionsChanged = oldWidth > 0 && (Math.abs(newWidth - oldWidth) > 10 || Math.abs(newHeight - oldHeight) > 10);

                      if (dimensionsChanged) {
                        const currentVisualScale = leftZoom.getVisualScale();

                        // Adjust zoom in two cases:
                        // 1. Switching to original image (preserve visual scale)
                        // 2. Loading new regular image with preserved zoom from previous image
                        if (leftImageStateRef.current === 'switching-to-original' ||
                            (leftImageStateRef.current === 'large' && currentVisualScale > 1.2)) {
                          // Adjust zoom to maintain visual continuity
                          leftZoom.adjustZoomForNewImage(oldWidth, oldHeight, newWidth, newHeight);
                        }
                      }

                      if (leftImageStateRef.current === 'switching-to-original') {
                        leftImageStateRef.current = 'original';
                      }

                      // Update stored dimensions
                      leftDimensionsRef.current = { width: newWidth, height: newHeight };
                    }}
                    style={{
                      transform: `translate(${leftZoom.zoomState.offsetX}px, ${leftZoom.zoomState.offsetY}px) scale(${leftZoom.zoomState.scale})`,
                      cursor: leftZoom.zoomState.scale > 1 ? 'grab' : 'default',
                    }}
                    draggable={false}
                  />
                )}
                {(asyncLeft.state === 'generating' || asyncLeft.state === 'loading') && (
                  <div className="preview-status-box">
                    <div className="loading-spinner" />
                    <span className="preview-status-label">
                      {asyncLeft.state === 'generating' ? 'Generating…' : 'Loading…'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="panel-info">
              <div className="info-row">
                <strong>Target:</strong> {leftImage.target_name}
              </div>
              <div className="info-row">
                <strong>Filter:</strong> {leftImage.filter_name || 'None'}
              </div>
              <div className="info-row">
                <strong>Date:</strong> {formatDate(leftImage.acquired_date)}
              </div>
              {leftImage.metadata?.HFR && (
                <div className="info-row">
                  <strong>HFR:</strong> {leftImage.metadata.HFR.toFixed(2)}
                </div>
              )}
              {leftImage.metadata?.DetectedStars !== undefined && (
                <div className="info-row">
                  <strong>Stars:</strong> {leftImage.metadata.DetectedStars}
                </div>
              )}
            </div>

            <div className="panel-actions">
              <button onClick={() => onGradeLeft('accepted')} className="action-button accept" disabled={!canGrade}>
                Accept (1)
              </button>
              <button onClick={() => onGradeLeft('rejected')} className="action-button reject" disabled={!canGrade}>
                Reject (2)
              </button>
              <button onClick={() => onGradeLeft('pending')} className="action-button pending" disabled={!canGrade}>
                Unmark (3)
              </button>
            </div>
          </div>

          {/* Right Image */}
          <div className="comparison-panel">
            {rightImage ? (
              <>
                <div className="panel-header">
                  <div className="panel-title-row">
                    <h3>Right Image {isRightImageFetching && '(Loading...)'}</h3>
                    <div className={`status-banner ${getStatusClass(rightImage)}`}>
                      {rightImage.grading_status === GradingStatus.Accepted && '✓ ACCEPTED'}
                      {rightImage.grading_status === GradingStatus.Rejected && '✗ REJECTED'}
                      {rightImage.grading_status === GradingStatus.Pending && '○ PENDING'}
                    </div>
                  </div>
                </div>
                
                <div className="panel-image">
                  <div 
                    className={`zoom-container ${rightZoom.hasOverflow ? 'zoomed' : ''}`}
                    ref={rightZoom.containerRef}
                    onWheel={handleRightZoom}
                    onMouseDown={(e) => {
                      if (!syncZoom) {
                        rightZoom.handleMouseDown(e);
                      } else {
                        // When synced, translate coordinates and apply to left image
                        const leftContainer = leftZoom.containerRef.current;
                        const rightContainer = rightZoom.containerRef.current;
                        if (leftContainer && rightContainer) {
                          const rightRect = rightContainer.getBoundingClientRect();
                          const leftRect = leftContainer.getBoundingClientRect();
                          
                          // Create a new mouse event with translated coordinates
                          const adjustedEvent = Object.create(e);
                          adjustedEvent.clientX = e.clientX - rightRect.left + leftRect.left;
                          adjustedEvent.clientY = e.clientY - rightRect.top + leftRect.top;
                          
                          leftZoom.handleMouseDown(adjustedEvent);
                        }
                      }
                    }}
                    onMouseMove={handleRightMouseMove}
                    onMouseUp={syncZoom ? leftZoom.handleMouseUp : rightZoom.handleMouseUp}
                    onMouseLeave={syncZoom ? leftZoom.handleMouseUp : rightZoom.handleMouseUp}
                    onTouchStart={(e) => routeRightTouch(e, 'start')}
                    onTouchMove={(e) => routeRightTouch(e, 'move')}
                    onTouchEnd={(e) => routeRightTouch(e, 'end')}
                    onTouchCancel={(e) => routeRightTouch(e, 'end')}
                  >
                    {asyncRight.state === 'error' ? (
                      <div className="comparison-image-error" style={{
                        width: '100%',
                        height: '100%',
                        minHeight: '400px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px'
                      }}>
                        <div style={{
                          textAlign: 'center',
                          color: 'var(--color-text-muted)'
                        }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: '12px' }}>
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                          </svg>
                          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-text-hint)' }}>{asyncRight.error ?? 'Image not available'}</p>
                        </div>
                      </div>
                    ) : (() => {
                      // Size is chosen at the top (rightSize) so the readiness
                      // poll addresses the same cache file as this <img>.
                      return (
                        <img
                          ref={rightZoom.imageRef}
                          src={asyncRight.src}
                          className={hideRightImage ? 'comparison-image-hidden' : undefined}
                          alt={`${rightImage.target_name} - ${rightImage.filter_name || 'No filter'}`}
                          onError={asyncRight.onError}
                          onLoad={(e) => {
                            asyncRight.onLoad();
                            setRightImageLoaded(true);
                            setLoadedRightSrc(asyncRight.src);

                            const img = e.currentTarget;
                            const newWidth = img.naturalWidth;
                            const newHeight = img.naturalHeight;
                            const oldWidth = rightDimensionsRef.current.width;
                            const oldHeight = rightDimensionsRef.current.height;

                            // Update image dimensions in zoom hook
                            const isShowingOriginal = useRightOriginal || (rightOriginalLoaded && targetRightZoomRef.current !== null && targetRightZoomRef.current > 1.0);
                            rightZoom.setImageDimensions(newWidth, newHeight, isShowingOriginal);
                            // Recalibrate pan constraints to the loaded bitmap
                            // even when nothing below adjusts (preserved zoom on
                            // a new image) — constraints follow stateDims.
                            rightZoom.notifyBitmapDimensions(newWidth, newHeight);

                            // Check if dimensions actually changed
                            const dimensionsChanged = oldWidth > 0 && (Math.abs(newWidth - oldWidth) > 10 || Math.abs(newHeight - oldHeight) > 10);

                            if (dimensionsChanged) {
                              const currentVisualScale = rightZoom.getVisualScale();

                              // Adjust zoom in two cases:
                              // 1. Switching to original image (preserve visual scale)
                              // 2. Loading new regular image with preserved zoom from previous image
                              if (rightImageStateRef.current === 'switching-to-original' ||
                                  (rightImageStateRef.current === 'large' && currentVisualScale > 1.2)) {
                                // Adjust zoom to maintain visual continuity
                                rightZoom.adjustZoomForNewImage(oldWidth, oldHeight, newWidth, newHeight);
                              }
                            }

                            if (rightImageStateRef.current === 'switching-to-original') {
                              rightImageStateRef.current = 'original';
                            }

                            // Update stored dimensions
                            rightDimensionsRef.current = { width: newWidth, height: newHeight };
                          }}
                          style={{
                            transform: `translate(${rightZoom.zoomState.offsetX}px, ${rightZoom.zoomState.offsetY}px) scale(${rightZoom.zoomState.scale})`,
                            cursor: rightZoom.zoomState.scale > 1 ? 'grab' : 'default',
                          }}
                          draggable={false}
                        />
                      );
                    })()}
                    {(asyncRight.state === 'generating' || asyncRight.state === 'loading') && (
                      <div className="preview-status-box">
                        <div className="loading-spinner" />
                        <span className="preview-status-label">
                          {asyncRight.state === 'generating' ? 'Generating…' : 'Loading…'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="panel-info">
                  <div className="info-row">
                    <strong>Target:</strong> {rightImage.target_name}
                  </div>
                  <div className="info-row">
                    <strong>Filter:</strong> {rightImage.filter_name || 'None'}
                  </div>
                  <div className="info-row">
                    <strong>Date:</strong> {formatDate(rightImage.acquired_date)}
                  </div>
                  {rightImage.metadata?.HFR && (
                    <div className="info-row">
                      <strong>HFR:</strong> {rightImage.metadata.HFR.toFixed(2)}
                    </div>
                  )}
                  {rightImage.metadata?.DetectedStars !== undefined && (
                    <div className="info-row">
                      <strong>Stars:</strong> {rightImage.metadata.DetectedStars}
                    </div>
                  )}
                </div>

                <div className="panel-actions">
                  <button onClick={() => onGradeRight('accepted')} className="action-button accept" disabled={!canGrade}>
                    Accept (7)
                  </button>
                  <button onClick={() => onGradeRight('rejected')} className="action-button reject" disabled={!canGrade}>
                    Reject (8)
                  </button>
                  <button onClick={() => onGradeRight('pending')} className="action-button pending" disabled={!canGrade}>
                    Unmark (9)
                  </button>
                </div>
              </>
            ) : (
              <div className="panel-empty">
                <p>No image selected for comparison</p>
                <button onClick={onSelectRightImage} className="select-button">
                  Select Image
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="comparison-shortcuts">
          <div className="shortcut-section">
            <h4>Navigation</h4>
            <span>ESC Close</span>
            <span>S Toggle Stars</span>
            <span>Z Toggle Sync Zoom</span>
            <span>W Swap Images</span>
            <span>→/K Next Right</span>
            <span>←/J Prev Right</span>
          </div>
          <div className="shortcut-section">
            <h4>Left Image</h4>
            <span>1 Accept</span>
            <span>2 Reject</span>
            <span>3 Unmark</span>
          </div>
          <div className="shortcut-section">
            <h4>Right Image</h4>
            <span>7 Accept</span>
            <span>8 Reject</span>
            <span>9 Unmark</span>
          </div>
        </div>
      </div>
    </div>
  );
}
